import { supabase } from '../../lib/supabase';
import { throwFunctionsInvokeError } from '../../lib/functionsInvoke';

type EdgeFunctionErrorBody = { error?: string };
type ConnectResponse = { integrationId?: string; error?: string };

export interface InstagramAccountOption {
    pageId: string;
    pageName: string;
    instagramBusinessAccountId: string;
    instagramUsername: string;
    profilePictureUrl?: string;
}

export interface InstagramConnectionStatus {
    id?: string;
    connected: boolean;
    accountName?: string;
    pageName?: string;
    instagramUsername?: string;
    expiresAt?: string;
    datasetId?: string;
}

export interface InstagramConnectPayload {
    pageId: string;
    pageName: string;
    instagramBusinessAccountId: string;
    instagramUsername: string;
    accessToken: string;
    expiresAt: string;
}

export const getInstagramConnections = async (organizationId: string): Promise<InstagramConnectionStatus[]> => {
    const { data, error } = await supabase
        .from('integrations')
        .select('id, name, status, config, credentials')
        .eq('organization_id', organizationId)
        .eq('type', 'instagram_dm')
        .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);

    return (data || []).map((row) => ({
        id: row.id,
        connected: row.status === 'connected',
        accountName: row.name,
        pageName: (row.config as Record<string, string>)?.page_name,
        instagramUsername: (row.config as Record<string, string>)?.instagram_username,
        expiresAt: (row.credentials as Record<string, string>)?.expires_at,
        datasetId: (row.config as Record<string, string>)?.dataset_id,
    }));
};

export const isValidInstagramUsername = (value?: string): boolean => {
    const handle = value?.trim();
    if (!handle || /^\d+$/.test(handle)) return false;
    if (/^instagram direct$/i.test(handle)) return false;
    return /^[a-z0-9._]{1,30}$/i.test(handle);
};

export const getInstagramDisplayHandle = (
    connection: Pick<InstagramConnectionStatus, 'instagramUsername' | 'accountName' | 'pageName'>,
): string => {
    const handle = [connection.instagramUsername, connection.accountName, connection.pageName]
        .find((value) => isValidInstagramUsername(value));
    return handle ? `@${handle}` : 'Conta conectada';
};

export const refreshInstagramContactAvatars = async (organizationId: string): Promise<number | null> => {
    try {
        const { data, error } = await supabase.functions.invoke('instagram-api', {
            body: { action: 'refresh-avatars', organizationId },
        });
        if (error || (data as EdgeFunctionErrorBody | null)?.error) return null;
        return (data as { updated?: number } | null)?.updated ?? 0;
    } catch {
        return null;
    }
};

export const refreshInstagramProfile = async (organizationId: string): Promise<string | null> => {
    const { data, error } = await supabase.functions.invoke('instagram-api', {
        body: { action: 'refresh-profile', organizationId },
    });

    if (error || (data as EdgeFunctionErrorBody | null)?.error) {
        return null;
    }

    return (data as { username?: string | null })?.username ?? null;
};

export const getInstagramConnection = async (
    organizationId: string
): Promise<InstagramConnectionStatus | null> => {
    const list = await getInstagramConnections(organizationId);
    return list.find((c) => c.connected) ?? list[0] ?? null;
};

export const removeInstagramIntegration = async (
    organizationId: string,
    integrationId?: string
): Promise<void> => {
    let query = supabase
        .from('integrations')
        .delete()
        .eq('organization_id', organizationId)
        .eq('type', 'instagram_dm');

    if (integrationId) {
        query = query.eq('id', integrationId);
    }

    const { error } = await query;
    if (error) throw new Error(error.message);
};

export const saveInstagramIntegration = async (
    payload: InstagramConnectPayload,
    organizationId: string
): Promise<string> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.functions.invoke('instagram-api', {
        body: {
            action: 'connect',
            organizationId,
            pageId: payload.pageId,
            pageName: payload.pageName,
            instagramBusinessAccountId: payload.instagramBusinessAccountId,
            instagramUsername: payload.instagramUsername,
            accessToken: payload.accessToken,
            expiresAt: payload.expiresAt,
        },
    });

    if (error || (data as ConnectResponse | null)?.error) {
        await throwFunctionsInvokeError(data, error, 'Erro ao salvar conexão Instagram');
    }

    const result = data as ConnectResponse | null;
    if (!result?.integrationId) {
        throw new Error(result?.error || 'Falha ao salvar integração Instagram');
    }

    return result.integrationId;
};

export const disconnectInstagramIntegration = async (
    organizationId: string,
    integrationId: string
): Promise<void> => {
    const { data, error } = await supabase.functions.invoke('instagram-api', {
        body: { action: 'disconnect', organizationId, integrationId },
    });

    if (error || (data as ConnectResponse | null)?.error) {
        await throwFunctionsInvokeError(data, error, 'Erro ao desconectar Instagram');
    }
};
